/**
 * Admin Traffic Quality service — any client, any campaign (no account scoping).
 * Method signatures mirror the customer trafficQualityService so the shared
 * TrafficQualityPage component can be reused with these fetchers injected.
 */

import api from '../api';
import type {
  TrafficQualityResponse,
  TrafficQualityPeriod,
  KeywordQuality,
  SourceQuality,
  FraudIp,
  FraudAlert,
  AccountTrafficQuality,
} from '../../types/trafficQuality';

const p = (period: TrafficQualityPeriod) => ({ params: { period } });
const BASE = '/api/admin/traffic-quality';

export const adminTrafficQualityService = {
  async getSummary(campaignId: number, period: TrafficQualityPeriod): Promise<TrafficQualityResponse> {
    const res = await api.get<TrafficQualityResponse>(`${BASE}/campaigns/${campaignId}`, p(period));
    return res.data;
  },
  async getBySource(campaignId: number, period: TrafficQualityPeriod): Promise<SourceQuality[]> {
    const res = await api.get<{ sources: SourceQuality[] }>(`${BASE}/campaigns/${campaignId}/by-source`, p(period));
    return res.data.sources;
  },
  async getByKeyword(campaignId: number, period: TrafficQualityPeriod): Promise<KeywordQuality[]> {
    const res = await api.get<{ keywords: KeywordQuality[] }>(`${BASE}/campaigns/${campaignId}/by-keyword`, p(period));
    return res.data.keywords;
  },
  async getTopSources(campaignId: number, period: TrafficQualityPeriod): Promise<FraudIp[]> {
    const res = await api.get<{ ips: FraudIp[] }>(`${BASE}/campaigns/${campaignId}/top-sources`, p(period));
    return res.data.ips;
  },
  async getAlerts(campaignId: number): Promise<FraudAlert[]> {
    const res = await api.get<{ alerts: FraudAlert[] }>(`${BASE}/campaigns/${campaignId}/alerts`);
    return res.data.alerts;
  },
  async getAccountSummary(accountId: number, period: TrafficQualityPeriod): Promise<AccountTrafficQuality> {
    const res = await api.get<AccountTrafficQuality>(`${BASE}/account-summary`, {
      params: { account_id: accountId, period },
    });
    return res.data;
  },
};

export default adminTrafficQualityService;
